import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import PinEntry from "./components/PinEntry";
import AlarmWatcher from "./components/AlarmWatcher";
import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import RoutineBuilder from "./pages/RoutineBuilder";
import BoardEditor from "./pages/BoardEditor";
import ChoiceEditor from "./pages/ChoiceEditor";
import ChoiceRunner from "./pages/ChoiceRunner";
import SharedRoutine from "./pages/SharedRoutine";
import NotesPage from "./pages/NotesPage";
import UserNotes from "./pages/UserNotes";
import TimerPage from "./pages/TimerPage";
import FirstThenPage from "./pages/FirstThenPage";
import PrivacyPage from "./pages/PrivacyPage";
import SharedBoard from "./pages/SharedBoard";
import CookieNotice from "./components/CookieNotice";
import CalmPage from "./pages/CalmPage";
import StoriesPage from "./pages/StoriesPage";
import GalleryPage from "./pages/GalleryPage";
import HandoverPage from "./pages/HandoverPage";
import StatusPage from "./pages/StatusPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TermsPage from "./pages/TermsPage";
import CarePlansPage from "./pages/CarePlansPage";
import PlanViewPage from "./pages/PlanViewPage";
import UserHome from "./pages/UserHome";
import RoutineRunner from "./pages/RoutineRunner";
import CommBoard from "./pages/CommBoard";
import Premium from "./pages/Premium";
import PaymentResult from "./pages/PaymentResult";

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <p className="text-xl font-bold text-muted-foreground">Loading…</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function RequireCarer({ children }) {
  const { user, carerUnlocked } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <p className="text-xl font-bold text-muted-foreground">Loading…</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.has_pin && !carerUnlocked) return <PinEntry />;
  return children;
}

function EveningOverlay() {
  const { evening } = useAuth();
  if (!evening) return null;
  return <div data-testid="evening-overlay" className="fixed inset-0 z-[70] bg-black/25 pointer-events-none" />;
}

function OfflineBanner() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  if (online) return null;
  return (
    <div data-testid="offline-banner" className="fixed top-2 left-1/2 -translate-x-1/2 z-[80] bg-[#2D3748] text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
      You're offline — showing saved data
    </div>
  );
}

export default function App() {
  useEffect(() => {
    document.body.classList.toggle("hc", localStorage.getItem("rb_hc") === "1");
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background text-foreground font-sans">
          <AlarmWatcher />
          <EveningOverlay />
          <OfflineBanner />
          <CookieNotice />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/app" element={<RequireCarer><Dashboard /></RequireCarer>} />
            <Route path="/app/routines/new" element={<RequireCarer><RoutineBuilder /></RequireCarer>} />
            <Route path="/app/routines/:id/edit" element={<RequireCarer><RoutineBuilder /></RequireCarer>} />
            <Route path="/app/boards/:id" element={<RequireCarer><BoardEditor /></RequireCarer>} />
            <Route path="/app/choices/new" element={<RequireCarer><ChoiceEditor /></RequireCarer>} />
            <Route path="/app/choices/:id/edit" element={<RequireCarer><ChoiceEditor /></RequireCarer>} />
            <Route path="/app/notes/:pid" element={<RequireCarer><NotesPage /></RequireCarer>} />
            <Route path="/app/gallery" element={<RequireCarer><GalleryPage /></RequireCarer>} />
            <Route path="/app/handover/:pid" element={<RequireCarer><HandoverPage /></RequireCarer>} />
            <Route path="/app/care-plans/:pid" element={<RequireCarer><CarePlansPage /></RequireCarer>} />
            <Route path="/use" element={<RequireAuth><UserHome /></RequireAuth>} />
            <Route path="/use/routine/:id" element={<RequireAuth><RoutineRunner /></RequireAuth>} />
            <Route path="/use/board" element={<RequireAuth><CommBoard /></RequireAuth>} />
            <Route path="/use/choice/:id" element={<RequireAuth><ChoiceRunner /></RequireAuth>} />
            <Route path="/use/notes" element={<RequireAuth><UserNotes /></RequireAuth>} />
            <Route path="/use/timer" element={<RequireAuth><TimerPage /></RequireAuth>} />
            <Route path="/use/firstthen" element={<RequireAuth><FirstThenPage /></RequireAuth>} />
            <Route path="/use/calm" element={<RequireAuth><CalmPage /></RequireAuth>} />
            <Route path="/use/stories" element={<RequireAuth><StoriesPage /></RequireAuth>} />
            <Route path="/use/stories/:id" element={<RequireAuth><StoriesPage /></RequireAuth>} />
            <Route path="/use/plan" element={<RequireAuth><PlanViewPage /></RequireAuth>} />
            <Route path="/shared/:token" element={<SharedRoutine />} />
            <Route path="/shared-board/:token" element={<SharedBoard />} />
            <Route path="/status/:token" element={<StatusPage />} />
            <Route path="/premium" element={<RequireCarer><Premium /></RequireCarer>} />
            <Route path="/payment/success" element={<RequireCarer><PaymentResult /></RequireCarer>} />
            <Route path="/payment/cancel" element={<RequireCarer><PaymentResult /></RequireCarer>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}
